import React from "react";
import { Box, Container, Typography, Divider, Link } from "@mui/material";

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ py: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Effective Date: June 2, 2026
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph>
          Gleason Development LLC ("we," "us," or "our") operates the RoomRoster
          property management platform. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use
          our service. By using RoomRoster, you agree to the practices described
          in this policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          1. Information We Collect
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
          Account Information
        </Typography>
        <Typography variant="body1" paragraph>
          When you register or are invited to use RoomRoster, we collect your
          name, email address, phone number, and the role assigned to your
          account (e.g., manager, maintenance, housekeeping). Accounts are
          created by property managers on behalf of staff; self-registration is
          only permitted through a valid invitation link.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          Phone Numbers
        </Typography>
        <Typography variant="body1" paragraph>
          We collect phone numbers from staff members, guests, and contacts
          entered into the platform. Phone numbers are used for staff
          notifications, guest communication, vendor coordination, and
          sales-related outreach. By providing a phone number, you consent to
          being contacted via that number for the purposes described above.
          Standard messaging and carrier rates may apply. You may opt out of
          non-essential communications at any time by contacting us at{" "}
          <Link href="mailto:support@gleasondevelopment.com">
            support@gleasondevelopment.com
          </Link>
          .
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          Property and Operational Data
        </Typography>
        <Typography variant="body1" paragraph>
          We collect data you and your team enter into the platform, including
          room status records, work orders, maintenance and housekeeping
          requirements, purchase orders, employee schedules, vendor information,
          and property-specific notes or checklists. This data belongs to your
          organization and is processed on your behalf.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          Usage Data
        </Typography>
        <Typography variant="body1" paragraph>
          We automatically collect information about how you interact with
          RoomRoster, including log data, browser type, device identifiers, IP
          address, pages visited, and timestamps of activity. This information
          is used to maintain the security and performance of the service.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          Images and Files
        </Typography>
        <Typography variant="body1" paragraph>
          Users may upload images related to room conditions, work orders, or
          property issues. These files are stored in Google Firebase Storage
          and associated with the relevant property record.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" component="div">
          <ul>
            <li>To authenticate users and manage account access</li>
            <li>To provide and operate the RoomRoster platform</li>
            <li>To send invitation emails and account-related notifications</li>
            <li>To send staff notifications via phone (SMS or call)</li>
            <li>To facilitate guest communication on behalf of your property</li>
            <li>To support sales outreach and vendor coordination</li>
            <li>To generate reports and summaries for your property</li>
            <li>To maintain service reliability, security, and performance</li>
            <li>To comply with legal obligations</li>
          </ul>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          3. How We Share Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell, rent, or trade your personal information. We may share
          data in the following limited circumstances:
        </Typography>
        <Typography variant="body1" component="div">
          <ul>
            <li>
              <strong>Service Providers:</strong> We use Google Firebase
              (Authentication, Firestore, Cloud Functions, Storage, and Hosting)
              to operate the platform. Google processes data on our behalf
              subject to their privacy and security standards.
            </li>
            <li>
              <strong>Within Your Organization:</strong> Data you enter is
              visible to other authorized users at your property according to
              their assigned role.
            </li>
            <li>
              <strong>Legal Requirements:</strong> We may disclose information
              if required by law, court order, or to protect the rights and
              safety of our users or the public.
            </li>
          </ul>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          4. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your data for as long as your account is active or as
          needed to provide the service. Property operational data is retained
          at the direction of the account owner. You may request deletion of
          your personal account data by contacting us at the address below.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          5. Security
        </Typography>
        <Typography variant="body1" paragraph>
          We use Firebase Authentication for secure login, role-based access
          controls to limit data visibility, and HTTPS encryption for all data
          in transit. Firebase infrastructure provides additional protections
          including data encryption at rest. No method of transmission over the
          internet is 100% secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          6. Your Rights
        </Typography>
        <Typography variant="body1" paragraph>
          You have the right to access the personal information we hold about
          you, request corrections to inaccurate data, and request deletion of
          your account. To exercise any of these rights, contact us at{" "}
          <Link href="mailto:support@gleasondevelopment.com">
            support@gleasondevelopment.com
          </Link>
          . We will respond within a reasonable timeframe.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          7. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          RoomRoster is a professional platform intended for business use only.
          We do not knowingly collect personal information from individuals
          under the age of 18. If you believe a minor has provided us with
          personal information, please contact us so we can delete it.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          8. Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. We will notify
          users of material changes by updating the effective date at the top
          of this page. Continued use of RoomRoster after changes constitutes
          acceptance of the updated policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          9. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions or concerns about this Privacy Policy, please
          contact us at:
        </Typography>
        <Typography variant="body1">
          <strong>Gleason Development LLC</strong>
          <br />
          Email:{" "}
          <Link href="mailto:support@gleasondevelopment.com">
            support@gleasondevelopment.com
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
